import { useState, useEffect, useRef } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import notificationSound from '../assets/notification.mp3';

const playNotificationSound = () => {
    try {
        const audio = new Audio(notificationSound);
        audio.play().catch(e => console.error("Audio playback failed because user hasn't interacted with document yet", e));
    } catch (e) {
        console.error("Audio playback failed", e);
    }
};

/**
 * Hook to listen for unread community notifications in real-time.
 * Mirrors the pattern of the existing useUnreadCount hook.
 */
export default function useCommunityNotifications() {
    const { currentUser } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const lastUnreadTimestampRef = useRef(0);
    const initialLoadRef = useRef(true);

    useEffect(() => {
        if (!currentUser) return;

        const db = getDatabase();
        const notificationsRef = ref(db, `communityNotifications/${currentUser.uid}`);

        const unsubscribe = onValue(notificationsRef, (snapshot) => {
            if (!snapshot.exists()) {
                setUnreadCount(0);
                initialLoadRef.current = false;
                return;
            }

            let count = 0;
            let latestTimestamp = 0;

            snapshot.forEach((childSnapshot) => {
                const notification = childSnapshot.val();
                
                if (!notification.isRead) {
                    count++;
                    const msgTime = notification.createdAt || 0;
                    if (msgTime > latestTimestamp) {
                        latestTimestamp = msgTime;
                    }
                }
            });

            setUnreadCount(count);

            if (!initialLoadRef.current) {
                // Only play sound for new notifications that arrived after initial load
                if (latestTimestamp > lastUnreadTimestampRef.current && count > 0) {
                    playNotificationSound();
                }
            } else {
                initialLoadRef.current = false;
            }
            
            lastUnreadTimestampRef.current = latestTimestamp;
        });

        return () => unsubscribe();
    }, [currentUser]);

    return unreadCount;
}
